import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AggregateTable, ControlledTooltip, Header, NoContent } from 'components'
import FilterIcon from 'components/atoms/Icons/Controls/Filter'
import { useSearchFilter } from 'views/providers'
import { ROUTES } from 'constants/routes'
import { jsonStringSearch } from 'utils/string'
import { agencyApiEvents } from 'modules/api/agency'
import { TAgencySegment } from 'modules/api/agency/types'
import { useSegments } from '../../SegmentsProvider'
import DeleteSegmentConfirmation from '../DeleteSegmentConfirmation/DeleteSegmentConfirmation'
import styles from './SegmentsTable.module.scss'

const columns = [
  {
    Header: 'Segment Name',
    accessor: 'name',
    style: {
      minWidth: '400px',
      textAlign: 'left',
      justifyContent: 'flex-start',
      paddingLeft: '16px',
    },
  },
  {
    Header: '',
    accessor: 'actions',
    style: {
      justifyContent: 'flex-end',
    },
  },
]

const SegmentsTable = (): React.ReactElement => {
  const dispatch = useDispatch()
  const { segments, redirect } = useSegments()
  const { searchQuery } = useSearchFilter()

  const [segmentToDelete, setSegmentToDelete] = useState<TAgencySegment | null>(null)

  const handleDeleteSegment = useCallback(
    ({ id, isDeletable }: TAgencySegment) => {
      isDeletable && dispatch(agencyApiEvents.deleteSegment_request({ segmentId: id }))
      setSegmentToDelete(null)
    },
    [dispatch],
  )

  const editSegment = useCallback((id: string) => redirect(ROUTES.segmentView.replace(':id', id)), [redirect])

  const rows = useMemo(
    () =>
      segments
        .filter(segment => !searchQuery || jsonStringSearch(searchQuery, segment))
        .map(segment => ({
          id: segment.id,
          name: segment.name,
          actions: (
            <>
              {segment.isDeletable && (
                <div>
                  <ControlledTooltip body="Delete segment">
                    <button className={styles['delete-segment']} onClick={() => setSegmentToDelete(segment)} />
                  </ControlledTooltip>
                </div>
              )}
              <div>
                <ControlledTooltip body="Edit segment">
                  <button className={styles['edit-segment']} onClick={() => editSegment(segment.id)} />
                </ControlledTooltip>
              </div>
            </>
          ),
        })),
    [segments, searchQuery, editSegment],
  )

  return (
    <>
      <div className={styles.root}>
        <Header icon={<FilterIcon />} text="Segments" />

        {rows.length > 0 ? (
          <AggregateTable rowHeight={80} columns={columns} data={rows} interactive={false} />
        ) : (
          <NoContent
            heading={
              <p>
                No segments found.{' '}
                <button className={styles['no-segments-button']} onClick={() => editSegment('new')}>
                  Add a new segment
                </button>
              </p>
            }
          />
        )}
      </div>
      <DeleteSegmentConfirmation
        isOpen={segmentToDelete !== null}
        segmentName={segmentToDelete?.name}
        onConfirm={() => segmentToDelete !== null && handleDeleteSegment(segmentToDelete)}
        onCancel={() => setSegmentToDelete(null)}
      />
    </>
  )
}

export default SegmentsTable
